import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { AlertCircle, RotateCcw } from "lucide-react";
import NovariMarkLoader from "./NovariMarkLoader";
import { Button } from "./button";
import { useLoadingScreenAnalytics } from "@/hooks/useLoadingScreenAnalytics";
import { track } from "@/lib/analytics/analytics";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

interface NovariLoadingScreenProps {
  message?: string;
  timeout?: number;
  onTimeout?: () => void;
}

/** The same token-based identity as the boot splash and inline LoadingState.
 * Loading is indeterminate: no simulated percentage or decorative progress. */
export default function NovariLoadingScreen({
  message,
  timeout = 10000,
  onTimeout,
}: NovariLoadingScreenProps) {
  const reduce = useReducedMotion();
  const [isTimedOut, setIsTimedOut] = useState(false);
  useLoadingScreenAnalytics(true, "novari_loading_screen", {
    message: message ?? null,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTimedOut(true);
      track(ANALYTICS_EVENTS.LOADING_SCREEN_TIMEOUT, {
        screen: "novari_loading_screen",
        timeout_ms: timeout,
        retry_count: 0,
      });
      onTimeout?.();
    }, timeout);
    return () => clearTimeout(timer);
  }, [timeout, onTimeout]);

  return (
    <div
      className="fixed inset-0 z-celebration flex items-center justify-center p-6"
      style={{ background: "var(--bg-primary)" }}
      role={isTimedOut ? "alert" : "status"}
      aria-live="polite"
      aria-label={
        isTimedOut ? "Connection timeout" : message || "Loading Novari"
      }
    >
      <motion.div
        className="flex w-full max-w-sm flex-col items-center gap-5 text-center"
        initial={reduce ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduce ? 0 : 0.18 }}
      >
        {isTimedOut ? (
          <>
            <div
              className="grid h-14 w-14 place-items-center rounded-[var(--radius-control)]"
              style={{
                background: "var(--accent-error-soft)",
                color: "var(--accent-error)",
              }}
            >
              <AlertCircle className="h-7 w-7" aria-hidden="true" />
            </div>
            <h2 className="text-lg font-semibold text-ink">
              This is taking longer than expected
            </h2>
            <p className="text-sm leading-relaxed text-ink-2">
              Check your connection and try again.
            </p>
            <Button onClick={() => window.location.reload()}>
              <RotateCcw aria-hidden="true" />
              Try again
            </Button>
            <Button variant="ghost" onClick={() => window.history.back()}>
              Go back
            </Button>
          </>
        ) : (
          <>
            <NovariMarkLoader size={48} cycle={false} />
            <span className="font-display text-2xl font-semibold tracking-tight text-ink">
              Novari
            </span>
            <span className="text-sm leading-relaxed text-ink-2">
              {message || "Preparing your study space"}
            </span>
          </>
        )}
      </motion.div>
    </div>
  );
}
